import { type System, SystemType } from '@/api/types'

type InstanceLike = {
  id?: string
  system_id?: string | null
  ingest_tank_readings?: boolean
}

/**
 * Resolve the system type of a connected (local) instance:
 * instance_id -> SystemInstance.system_id -> System.type. Returns undefined when
 * the instance or its system can't be resolved (or the systems list isn't loaded).
 */
export const connectedSystemType = (
  instanceId: string | null | undefined,
  instances: ReadonlyArray<InstanceLike>,
  systems: System[] | undefined,
): string | undefined =>
  systems?.find(
    (s) => s._id === instances.find((i) => i.id === instanceId)?.system_id,
  )?.type

/**
 * My instances that can take a reading feed. Only S&D accepts a reading upload, so the router
 * drops anything else -- offering a non-S&D instance would be a switch that does nothing.
 */
export const sdInstances = <T extends InstanceLike>(
  instances: ReadonlyArray<T>,
  systems: System[] | undefined,
): T[] =>
  instances.filter(
    (i) => connectedSystemType(i.id, instances, systems) === SystemType.SND,
  )

/**
 * Whether the send-readings toggle should be locked. I can only forward readings I actually
 * have, and Crossroads only has them when an S&D instance is pushing them in -- that is the
 * instance's "Send Tank Readings" setting (`ingest_tank_readings`). Unlocked while the systems
 * list is still loading so a valid tenant is never briefly false-disabled.
 */
export const isSendLocked = (
  instances: ReadonlyArray<InstanceLike>,
  systems: System[] | undefined,
): boolean =>
  !!systems &&
  !sdInstances(instances, systems).some((i) => i.ingest_tank_readings)
